/*
 * Copyright (c) 2025-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
    ViewContainerRef
} from '@angular/core';
import {ColDef, ColGroupDef, ICellEditorParams} from 'ag-grid-community';
import {ICellEditorAngularComp} from 'ag-grid-angular';
import {ValidationService} from '../../service/validation.service';
import {HoleCriteriaEditorComponent} from './hole-criteria-editor.component';
import {FieldRule} from '../../model/validation.model';
import {CommonHelperService} from '../../service/common-helper.service';
import {
    buildConglomerationFromRow,
    parseConglomerationToRow,
    updateConglomerationSegmentOnRow
} from '../../utils/conglomeration.util';
import {isRowWriteAllowedByMetadata} from '../../utils/row-write-control.util';

/** Per-column answers cached by {@link ViewDetailContentComponent.columnFacts}. */
interface ColumnFacts {
    holdsSubrowCell?: boolean;
    /** The option list the allowed values were built from, so a column offered new options is judged against them. */
    validOptionSource?: any;
    validOptions?: Set<any>;
}

@Component({
    selector: 'app-view-detail-content',
    template: `
        @if (childObjects && childObjects.length) {
          <p-tabs class="detail-tabs"
            [value]="activeChildIndex"
            (valueChange)="onTabChange($event)">
            <p-tablist>
              <p-tab [value]="-1">Main</p-tab>
              @for (child of childObjects; track child; let idx = $index) {
                <p-tab [value]="idx">
                  {{child.headerName}}
                </p-tab>
              }
            </p-tablist>
          </p-tabs>
        }

        @if (activeChildIndex === -1) {
          <div class="content">
            @for (row of rows; track row) {
              <div class="detail-row">
                <!-- Two-column rows -->
                @if (row.length > 1) {
                  @for (col of row; track col) {
                    <div class="detail-field detail-field-half">
                      <div class="detail-label">{{ col.headerName }}:</div>
                      <div class="detail-value"
                        [ngClass]="{
                          'modified-row': getChangeMessage(col),
                          'error-row': getValidationMessage(col),
                          'readonly-field': isReadonlyField(col)
                        }">
                        @if (isTemplateField(col)) {
                          @if (isLargeTextField(col)) {
                            <textarea
                              pInputTextarea
                              [rows]="5"
                              [readonly]="isReadonlyField(col)"
                              [ngModel]="getFieldDisplayValue(col)"
                              (ngModelChange)="onSimpleFieldChange(col, $event)">
                            </textarea>
                          } @else {
                            <input
                              pInputText
                              [type]="getInputType(col)"
                              [readonly]="isReadonlyField(col)"
                              [ngModel]="getFieldDisplayValue(col)"
                              (ngModelChange)="onSimpleFieldChange(col, $event)">
                            }
                          } @else {
                            <div #container>
                              <!-- Dynamic editors (selects, images, attachments, etc.) are injected here -->
                            </div>
                          }
                          <div class="orig-content">{{ getChangeMessage(col) }}</div>
                          <div class="error-content">{{ getValidationMessage(col) }}</div>
                        </div>
                      </div>
                    }
                  }

                <!-- Single-column rows -->
                @if (row.length === 1) {
                  <div class="detail-field"
                    [ngClass]="getSingleColumnDetailFieldClass(row[0])">
                    <div class="detail-label">{{ row[0].headerName }}:</div>
                    @if (!hasSubrowCell(row[0])) {
                      <div class="detail-value"
                        [ngClass]="{
                          'modified-row': getChangeMessage(row[0]),
                          'error-row': getValidationMessage(row[0]),
                          'readonly-field': isReadonlyField(row[0])
                        }">
                        @if (isTemplateField(row[0])) {
                          @if (isLargeTextField(row[0])) {
                            <textarea
                              pInputTextarea
                              [rows]="5"
                              [readonly]="isReadonlyField(row[0])"
                              [ngModel]="getFieldDisplayValue(row[0])"
                              (ngModelChange)="onSimpleFieldChange(row[0], $event)">
                            </textarea>
                          } @else {
                            <input
                              pInputText
                              [type]="getInputType(row[0])"
                              [readonly]="isReadonlyField(row[0])"
                              [ngModel]="getFieldDisplayValue(row[0])"
                              (ngModelChange)="onSimpleFieldChange(row[0], $event)">
                            }
                          } @else {
                            <div #container>
                              <!-- Dynamic editors (selects, images, attachments, etc.) are injected here -->
                            </div>
                          }
                          <div class="orig-content">{{ getChangeMessage(row[0]) }}</div>
                          <div class="error-content">{{ getValidationMessage(row[0]) }}</div>
                        </div>
                      } @else {
                        <div class="detail-value">
                            <app-child-grid
                                [colGroup]="row[0]"
                                [rowData]="rowData"
                                [updatedRowData]="updatedRowData"
                                [hasAdminPermission]="hasAdminPermission"
                                [fieldRulesByFieldName]="fieldRulesByFieldName"
                                (dataChange)="onChildGridChange($event)">
                            </app-child-grid>
                        </div>
                      }
                    </div>
                  }
              </div>
            }
          </div>
        }

        <!-- Child object tabs -->
        @for (child of childObjects; track child; let idx = $index) {
          @if (activeChildIndex === idx) {
            <div class="content">
              <app-view-detail-content
                [cols]="child.children"
                [hasAdminPermission]="hasAdminPermission"
                [hasPurchAdminPermission]="hasPurchAdminPermission"
                [hasTierNAdminPermission]="hasTierNAdminPermission"
                [fieldRulesByFieldName]="fieldRulesByFieldName"
                [rowData]="rowData"
                [originalRowData]="originalRowData"
                [updatedRowData]="updatedRowData"
                [editorRefs]="editorRefs"
                (dataChange)="onChildChange($event)">
              </app-view-detail-content>
            </div>
          }
        }
        `,
    styleUrls: [`./view-detail-dialog.component.scss`],
    standalone: false
})
export class ViewDetailContentComponent implements AfterViewInit {
    @Input() cols!: (ColDef | ColGroupDef)[];
    @Input() rowData!: any;
    @Input() originalRowData!: any;
    @Input() updatedRowData!: any;
    @Input() hasAdminPermission: boolean = false;
    @Input() hasPurchAdminPermission: boolean = false;
    @Input() hasTierNAdminPermission: boolean = false;
    @Input() fieldRulesByFieldName: Map<string, FieldRule> = new Map<string, FieldRule>();
    @Input() editorRefs!: { [field: string]: ICellEditorAngularComp };
    @Output() dataChange = new EventEmitter<any>();

    @ViewChildren('container', { read: ViewContainerRef }) containers!: QueryList<ViewContainerRef>;
    activeChildIndex = -1;
    rows: ColDef[][] = [];
    childObjects: ColGroupDef[] = [];
    /** Direct refs to conglomeration input elements so we can update their value when constituents change */
    private conglomerationInputRefs: { [fieldName: string]: HTMLInputElement } = {};
    /**
     * Answers that belong to a column definition rather than to the row being edited, kept so they are worked out
     * once instead of on every change-detection pass.
     *
     * <p>This component runs default change detection and binds getValidationMessage and hasSubrowCell from its
     * template for every column, so both run on every keystroke, timer and request completion for as long as the
     * dialog is open. Only entries that cannot go stale belong here: anything derived from `updatedRowData` must
     * stay uncached, because that object is mutated in place rather than replaced and there is no change to
     * invalidate on.
     */
    private readonly columnFacts = new WeakMap<ColDef | ColGroupDef, ColumnFacts>();

    constructor(
        private validationService: ValidationService,
        private ngZone: NgZone,
        private elementRef: ElementRef
    ) {}

    onTabChange(value: string | number): void {
        this.activeChildIndex = +value;
        // Tab switches recreate dynamic editor hosts; repopulate after the view updates.
        setTimeout(() => this.populateData(), 0);
    }

    /**
     * Handles data changes from nested child components.
     * Updates the local updatedRowData and propagates the change to the parent.
     */
    onChildChange(event: any) {
        this.updatedRowData = event;
        this.dataChange.emit(this.updatedRowData);
    }

    /**
     * PPCO fields require PURCH_ADMIN permission. Tier-N Resilient fields require COMPONENT_TIER_N_ADMIN.
     * Other fields follow the description: a catalog lock marks them read-only, and a matching
     * editRolesRequired leaves them editable.
     */
    isFieldEditable(field: string | undefined): boolean {
        if (field && field.startsWith('ppco.')) {
            return this.hasPurchAdminPermission;
        }
        if (field && (field === 'tierNResilient' || field.startsWith('tierNResilient.') || field === 'tierNResilientId')) {
            return this.hasTierNAdminPermission;
        }
        return true;
    }

    /** Schema read-only, or a field the description did not explicitly open for this user. */
    private isDetailFieldReadOnly(col: ColDef): boolean {
        const sourceRow = this.updatedRowData ?? this.rowData;
        if (this.isSpecialPermissionField(col.field)) {
            return !this.isFieldEditable(col.field) || !isRowWriteAllowedByMetadata(col, sourceRow);
        }
        const schemaReadOnly = (col as ColDef & {esciSchemaReadOnly?: boolean}).esciSchemaReadOnly;
        // The main grid marks every non-admin column editable:false. A description that unlocked the
        // column (editRolesRequired) still has to accept input here.
        if (schemaReadOnly === false) {
            return !isRowWriteAllowedByMetadata(col, sourceRow);
        }
        if (schemaReadOnly === true || col.editable === false) {
            return true;
        }
        if (!this.hasAdminPermission && schemaReadOnly !== false) {
            return true;
        }
        return !isRowWriteAllowedByMetadata(col, sourceRow);
    }

    private isSpecialPermissionField(field: string | undefined): boolean {
        return !!field && (field.startsWith('ppco.') || field === 'tierNResilient' ||
            field.startsWith('tierNResilient.') || field === 'tierNResilientId');
    }

    /**
     * Remove read-only fields from tab order so users do not tab into date spinners, PrimeNG triggers, etc.
     */
    private excludeReadOnlySubtreeFromTabOrder(root: HTMLElement): void {
        root.querySelectorAll( 'input, select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])')
            .forEach((el) => {(el as HTMLElement).tabIndex = -1;});
    }

    // IntelliJ thinks this isn't being called, but it will be called by angular anytime
    // one of the @Input fields get updated.
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['cols'] && this.cols) {
            this.rebuildRows();
            if (this.containers) { // Only if the view is already initialized
                this.populateData();
            }
        }
    }

    ngAfterViewInit(): void {
        // Now this.containers is populated
        this.populateData();
    }

    isTemplateField(col: ColDef): boolean {
        if (!col?.field) {
            return false;
        }
        const memberType = col.cellEditorParams?.memberType;
        const editor = col.cellEditor as any;
        const isAngularEditorComponent = !!editor && typeof editor !== 'string' && !!editor.ɵcmp;
        return memberType === 'largeText' || !editor || ['text', 'date', 'number'].includes(memberType) ||
            typeof editor === 'string' || !isAngularEditorComponent ||
            (!this.isFieldEditable(col.field) && !['image', 'image_list', 'attachment'].includes(memberType));
    }

    isLargeTextField(col: ColDef): boolean {
        return col.cellEditorParams?.memberType === 'largeText';
    }

    isReadonlyField(col: ColDef): boolean {
        return this.isDetailFieldReadOnly(col);
    }

    getInputType(col: ColDef): string {
        const memberType = col.cellEditorParams?.memberType;
        if (memberType === 'date') {
            return 'date';
        }
        if (memberType === 'number') {
            return 'number';
        }
        return 'text';
    }

    getFieldDisplayValue(col: ColDef): any {
        if (!col?.field) {
            return '';
        }
        let value = this.getColumnValue(col, this.updatedRowData ?? this.rowData);
        const memberType = col.cellEditorParams?.memberType;

        if (memberType === 'date') {
            if (value instanceof Date) {
                return value.toISOString().substring(0, 10);
            }
            if (typeof value === 'string') {
                return value.substring(0, 10);
            }
        }
        if (memberType === 'objectDropdown' && col.valueFormatter && col.valueFormatter instanceof Function) {
            value = col.valueFormatter({value: value} as any);
        }
        return value ?? '';
    }

    private getColumnValue(col: ColDef, row: any): any {
        if (typeof col.valueGetter === 'function') {
            return col.valueGetter({data: row, colDef: col} as any);
        }
        return CommonHelperService.getDeepValue(row, col.field);
    }

    private setColumnValue(col: ColDef, row: any, newValue: any): void {
        if (typeof col.valueSetter === 'function') {
            col.valueSetter({data: row, colDef: col, newValue} as any);
        } else {
            CommonHelperService.setDeepValue(row, col.field, newValue);
        }
    }

    private static getEditorRefKey(col: ColDef): string {
        return col.colId || col.field || '';
    }

    onSimpleFieldChange(col: ColDef, newValue: any): void {
        if (!col?.field || this.isReadonlyField(col)) {
            return;
        }
        const memberType = col.cellEditorParams?.memberType;
        if (memberType === 'number') {
            const parsed = newValue === '' ? null : parseFloat(newValue);
            CommonHelperService.setDeepValue(this.updatedRowData, col.field, Number.isNaN(parsed) ? null : parsed);
        } else {
            CommonHelperService.setDeepValue(this.updatedRowData, col.field, newValue);
        }
        this.dataChange.emit(this.updatedRowData);
    }

    rebuildRows() {
        this.rows = [];
        this.childObjects = [];
        let left = true;
        let currentRow: ColDef[];
        for (const col of this.cols) {
            if (col.headerName === 'Actions') {
                // The details view never shows the actions column
                continue;
            }
            if ('colId' in col && col.colId.endsWith('__placeholder')) {
                // Placeholder columns are not shown in the details view, they exist only to make the grid layout work
                // by showing a column of '...' when the column group is collapsed.
                continue;
            }
            if ('hide' in col && col.hide) {
                // dont show hidden columns
                continue;
            }
            // Skip display-only columns that don't have a 'field' (they have no data to show)
            if (!('children' in col) && !('field' in col && (col as ColDef).field)) {
                continue;
            }
            // If it has 'children' it's actually a ColGroupDef
            if ('children' in col && col.children) {
                // This is a composite object, it belongs in either its own tab, or as a child grid
                const group = col as ColGroupDef;
                if (this.hasSubrowCell(group)) {
                    // this is collection of sub rows, and should be displayed as a child grid
                    // Put this on the row available row all by itself:
                    this.rows.push([group]);
                    continue;
                }
                // This is an object that needs its own sub-tab (which may also have sub tabs)
                this.childObjects.push(group);
                continue;
            }
            const column = col as ColDef;
            if (column.cellEditorParams?.memberType === 'holeCriteria' || this.isWideTextBox(column)) {
                // Put Hole Criteria and wide text boxes in a separate full-width row.
                // Dropdown widths can grow to fit their options, but they remain in the two-column layout.
                this.rows.push([column]);
                continue;
            }
            if (left) {
                this.rows.push(currentRow = []);
            }
            currentRow.push(column);
            left = !left;
        }
    }

    populateData() {
        const sourceRow = this.updatedRowData ?? this.rowData;
        // Flatten the double array into a single array of all the ColDefs from 'rows'
        // But skip rows that contain child grids (ColGroupDefs with subrow-cell)
        const allCols: (ColDef | ColGroupDef)[] = [];
        for (const row of this.rows) {
            if (row.length === 1 && 'children' in row[0]) {
                const group = row[0] as ColGroupDef;
                // Skip child grid rows - they are handled by the template
                if (this.hasSubrowCell(group)) {
                    continue;
                }
            }
            allCols.push(...row);
        }

        // Only dynamic editors (non-template fields) need imperative component injection.
        const dynamicCols = allCols.flat()
                                   .filter((col: ColDef) => !!col?.field && !this.isTemplateField(col));
        this.conglomerationInputRefs = {};

        // Dynamically populate the '<div #container></div>' element with the data from the object:
        this.containers?.forEach((container, index) => {
            container.clear();
            const col = dynamicCols[index];
            if (!col) {
                return;
            }
            if (!('field' in col) || !col.field) {
                return;
            }
            const editor = col.cellEditor;
            this.editorRefs[ViewDetailContentComponent.getEditorRefKey(col)] = editor;

            // Check if editor type is a text field
            const memberType = col.cellEditorParams?.memberType;
            const conglomerationFields = col.cellEditorParams?.conglomerationFields as string | undefined;

            if (memberType === 'conglomeration' && conglomerationFields) {
                // Conglomeration field: show server value when present and valid; else build from constituents
                const row = sourceRow;
                const computed = buildConglomerationFromRow(row, conglomerationFields);
                const serverVal = row[col.field];
                const serverStr = serverVal != null ? String(serverVal).trim() : '';
                const displayValue = serverStr !== '' ? serverStr : computed;
                const inputElement = document.createElement('input');
                inputElement.type = 'text';
                inputElement.value = displayValue;
                inputElement.setAttribute('data-conglomeration-field', col.field);
                inputElement.classList.add('p-inputtext');
                this.conglomerationInputRefs[col.field] = inputElement;
                if (this.isDetailFieldReadOnly(col)) {
                    inputElement.setAttribute('readonly', 'true');
                    inputElement.classList.add('readonly-input');
                    inputElement.tabIndex = -1;
                } else {
                    inputElement.addEventListener('input', (event) => {
                        // When the user edits the conglomeration field, we update ALL the constituent fields
                        this.ngZone.run(() => {
                            const newValue = (event.target as HTMLInputElement).value;
                            parseConglomerationToRow(this.updatedRowData, conglomerationFields, newValue);
                            CommonHelperService.setDeepValue(this.updatedRowData, col.field, newValue);
                            this.refreshConstituentEditorsAfterConglomerationEdit(col.field, conglomerationFields);
                            this.dataChange.emit(this.updatedRowData);
                        });
                    });
                }
                container.element.nativeElement.appendChild(inputElement);
                return;
            }
            if (memberType === 'largeText') {
                // for large text fields, use a textarea
                const textarea = document.createElement('textarea');
                textarea.style.width = '95%';
                textarea.style.height = '100%';
                textarea.classList.add('p-inputtext'); // Add PrimeNG textarea class
                textarea.rows = 5;
                textarea.value = CommonHelperService.getDeepValue(sourceRow, col.field) || '';
                if (this.isDetailFieldReadOnly(col)) {
                    textarea.setAttribute('readonly', 'true');
                    textarea.classList.add('readonly-input');
                    textarea.tabIndex = -1;
                }
                container.element.nativeElement.appendChild(textarea);

                // add event listeners to watch for changes
                textarea.addEventListener('input', (event) => {
                    this.ngZone.run(() => {
                        CommonHelperService.setDeepValue(this.updatedRowData, col.field, (event.target as HTMLTextAreaElement).value);
                        this.updateParentConglomerationIfNeeded(col);
                        this.dataChange.emit(this.updatedRowData);
                    });
                });
            } else if (!editor || ['text', 'date', 'number'].includes(memberType) ||
                       (this.isDetailFieldReadOnly(col) && !['image', 'image_list', 'attachment'].includes(memberType))) {
                // We also treat all read-only editors as text fields (except images, imageList & attachments)
                // (avoiding how to set the readonly attribute on dropdowns, etc.)
                const inputElement = document.createElement('input');
                inputElement.type = ['text', 'date', 'number'].includes(memberType) ? memberType : 'text';
                let value = this.getColumnValue(col, sourceRow);

                if (inputElement.type === 'date') {
                    // Convert Date object to string in YYYY-MM-DD format
                    if (value instanceof Date) {
                        value = value.toISOString().substring(0, 10);
                    } else if (typeof value === 'string') {
                        // In case it's already an ISO string
                        value = value.substring(0, 10);
                    }
                } else if (memberType === 'objectDropdown') {
                    // For object dropdowns, display the allowedValueKey property if available
                    if (col.valueFormatter && col.valueFormatter instanceof Function) {
                        value = col.valueFormatter({value: value} as any);
                    }
                }

                inputElement.value = value || '';
                inputElement.classList.add('p-inputtext'); // Add PrimeNG input class
                if (this.isDetailFieldReadOnly(col)) {
                    inputElement.setAttribute('readonly', 'true');
                    inputElement.classList.add('readonly-input');
                    inputElement.tabIndex = -1;
                } else {
                    // add event listeners to watch for changes
                    inputElement.addEventListener('input', (event) => {
                        this.ngZone.run(() => {
                            const newValue = (event.target as HTMLInputElement).value;
                            if (memberType === 'number') {
                                const numValue = newValue === '' ? null : parseFloat(newValue);
                                CommonHelperService.setDeepValue(this.updatedRowData, col.field, numValue);
                            } else {
                                CommonHelperService.setDeepValue(this.updatedRowData, col.field, newValue);
                            }
                            this.updateParentConglomerationIfNeeded(col);
                            this.dataChange.emit(this.updatedRowData);
                        });
                    });
                }

                // Append the input element to the container
                container.element.nativeElement.appendChild(inputElement);
            } else if (editor && col.field) {
                const editorType = editor as any;
                // Some ag-grid editors are string/framework editors (not Angular component classes).
                // In detail view we fall back to text rendering for those to avoid createComponent runtime errors.
                if (typeof editorType === 'string' || !editorType?.ɵcmp) {
                    const inputElement = document.createElement('input');
                    inputElement.type = ['text', 'date', 'number'].includes(memberType) ? memberType : 'text';
                    inputElement.value = this.getFieldDisplayValue(col) || '';
                    inputElement.classList.add('p-inputtext');
                    inputElement.setAttribute('readonly', 'true');
                    inputElement.classList.add('readonly-input');
                    inputElement.tabIndex = -1;
                    container.element.nativeElement.appendChild(inputElement);
                    return;
                }
                // Handle complex editors like dropdowns, images, imageLists and attachments
                const componentRef = container.createComponent(editorType); // Component class, not a factory
                // Get the native DOM element of the container
                const nativeElement = container.element.nativeElement;

                (componentRef.instance as any).valueChange?.subscribe((newValue: any) => {
                    this.ngZone.run(() => {
                        this.setColumnValue(col, this.updatedRowData, newValue);
                        this.updateParentConglomerationIfNeeded(col);
                        this.updateDropdownDescription(col, newValue, nativeElement);
                        this.dataChange.emit(this.updatedRowData);
                    });
                });

                if (nativeElement) {
                    const params = this.buildEditorParams(
                        col,
                        this.getColumnValue(col, sourceRow),
                        sourceRow,
                        nativeElement
                    );

                    const editorInstance = componentRef.instance as ICellEditorAngularComp;

                    // Only call agInit if it exists
                    if (typeof editorInstance.agInit === 'function') {
                        // Set inlineMode if this is a HoleCriteriaEditorComponent
                        if (editorInstance instanceof HoleCriteriaEditorComponent) {
                            (params as any).inlineMode = true;
                        }
                        editorInstance.agInit(params);
                    }
                    if (this.isDetailFieldReadOnly(col)) {
                        editorInstance['readOnly'] = true;
                    }

                    this.editorRefs[ViewDetailContentComponent.getEditorRefKey(col)] = editorInstance;

                    // Append the component to the container
                    nativeElement.appendChild(componentRef.location.nativeElement);

                    if (this.isDetailFieldReadOnly(col)) {
                        // PrimeNG / nested controls mount after first CD — strip tab order once and on a short delay
                        const strip = () => this.excludeReadOnlySubtreeFromTabOrder(nativeElement);
                        strip();
                        setTimeout(strip, 0);
                        setTimeout(strip, 100);
                    }

                    // Add description for dropdown fields
                    const memberType = col.cellEditorParams?.memberType;
                    if (memberType === 'multiSelectDropdown' ||
                        memberType === 'multiSelectIdsDropdown' ||
                        memberType === 'objectDropdown') {
                        const currentValue = CommonHelperService.getDeepValue(sourceRow, col.field);
                        this.addDropdownDescription(col, currentValue, nativeElement);
                    }
                } else {
                    console.warn('Container element is not available for col:', col.field);
                }
            }
        });
    }

    /**
     * After a sub-field of a conglomeration is updated, rebuild the conglomeration field on the row
     * and update the conglomeration input element so it shows the new value.
     */
    private updateParentConglomerationIfNeeded(col: ColDef): void {
        let parent = (col.cellEditorParams as any)?.parentConglomeration as { memberName: string;
                                                                             conglomerationFields: string } | undefined;
        if (!parent?.conglomerationFields) {
            parent = this.getConglomerationParentForField(col.field);
        }
        if (parent?.conglomerationFields) {
            const fieldValue = CommonHelperService.getDeepValue(this.updatedRowData, col.field);
            updateConglomerationSegmentOnRow(
                this.updatedRowData,
                parent.memberName,
                parent.conglomerationFields,
                col.field,
                fieldValue
            );
            const updatedStr = CommonHelperService.getDeepValue(this.updatedRowData, parent.memberName);
            this.ngZone.runOutsideAngular(() => {
                setTimeout(() => this.ngZone.run(() => this.updateConglomerationInputDisplay(parent!.memberName,
                                                                                             updatedStr ?? '')), 0);
            });
        }
    }

    /**
     * Derive conglomeration parent from cols when cellEditorParams.parentConglomeration is missing
     * (e.g. when details view receives column defs without it).
     */
    private getConglomerationParentForField(fieldName: string): { memberName: string;
                                                                  conglomerationFields: string } | undefined {
        const allCols = this.rows.flat();
        const conglomerationCols = allCols.filter((c: ColDef) => c.cellEditorParams?.memberType === 'conglomeration');
        for (const conglomerationCol of conglomerationCols) {
            if (!conglomerationCol.field || !conglomerationCol.cellEditorParams?.conglomerationFields) {
                continue;
            }
            const conglomerationFields = (conglomerationCol.cellEditorParams as any).conglomerationFields as string;
            const fieldList = conglomerationFields.split(',')
                                                  .map((t: string) => t.trim())
                                                  .filter((t: string) => t !== '-');
            if (fieldList.includes(fieldName)) {
                return { memberName: conglomerationCol.field, conglomerationFields };
            }
        }
        return undefined;
    }

    /**
     * Update the conglomeration field's input element so it shows the current value
     * (e.g. when a constituent dropdown is changed and we rebuilt the conglomeration).
     */
    private updateConglomerationInputDisplay(conglomerationFieldName: string, value: string): void {
        const val = value ?? '';
        const inputByRef = this.conglomerationInputRefs[conglomerationFieldName];
        if (inputByRef) {
            inputByRef.value = val;
            return;
        }
        const escaped = CSS.escape(conglomerationFieldName);
        const selector = 'input[data-conglomeration-field="' + escaped + '"]';
        const inputByHost = this.elementRef.nativeElement.querySelector(selector) as HTMLInputElement | null;
        if (inputByHost) {
            inputByHost.value = val;
            return;
        }
        const containers = this.containers?.toArray();
        if (containers) {
            for (const container of containers) {
                const el = container?.element?.nativeElement;
                const input = el?.querySelector?.(selector) as HTMLInputElement | null;
                if (input) {
                    input.value = val;
                    return;
                }
            }
        }
    }

    /**
     * Build params for agInit. Used when creating editors and when refreshing after conglomeration edit.
     */
    private buildEditorParams(col: ColDef, value: any, data: any, nativeElement: HTMLElement | null): ICellEditorParams {
        return {
            value,
            data,
            colDef: col,
            context: {},
            node: null as any,
            column: null as any,
            api: null as any,
            columnApi: null as any,
            eventKey: null,
            cellStartedEdit: true,
            onKeyDown: () => {},
            stopEditing: () => {},
            parseValue: v => v,
            formatValue: v => String(v),
            rowIndex: 0,
            eGridCell: nativeElement,
        };
    }

    /**
     * After the user edits the conglomeration field, refresh each constituent's editor so it shows
     * the parsed value (e.g. TCR dropdown updates from A to B when specCode changes).
     */
    private refreshConstituentEditorsAfterConglomerationEdit(conglomerationFieldName: string,
                                                             conglomerationFields: string): void {
        const fieldNames = conglomerationFields.split(',')
                                               .map((t: string) => t.trim())
                                               .filter((t: string) => t !== '-' && t !== conglomerationFieldName);
        const allCols = this.rows.flat();
        for (const fieldName of fieldNames) {
            try {
                const constituentCol = allCols.find((c: ColDef) => c.field === fieldName);
                if (!constituentCol) {
                    continue;
                }
                const editorRef = this.editorRefs[ViewDetailContentComponent.getEditorRefKey(constituentCol)];
                if (!editorRef || typeof (editorRef as any).agInit !== 'function') {
                    continue;
                }
                const newValue = CommonHelperService.getDeepValue(this.updatedRowData, fieldName);
                const index = allCols.indexOf(constituentCol);
                const container = this.containers?.toArray()[index];
                const nativeElement = container?.element?.nativeElement ?? null;
                const params = this.buildEditorParams(constituentCol, newValue, this.updatedRowData, nativeElement);
                if (editorRef instanceof HoleCriteriaEditorComponent) {
                    (params as any).inlineMode = true;
                }
                (editorRef as any).agInit(params);
                if (nativeElement && (constituentCol.cellEditorParams?.memberType === 'multiSelectDropdown' ||
                                      constituentCol.cellEditorParams?.memberType === 'multiSelectIdsDropdown' ||
                                      constituentCol.cellEditorParams?.memberType === 'objectDropdown')) {
                    this.updateDropdownDescription(constituentCol, newValue, nativeElement);
                }
            } catch (e) {
                // Constituent may have an invalid value (e.g. not in dropdown list); skip refresh for this field
            }
        }
    }

    private addDropdownDescription(col: ColDef, value: any, containerElement: HTMLElement): void {
        const descriptionElement = document.createElement('div');
        descriptionElement.className = 'dropdown-description';
        descriptionElement.style.fontSize = '0.85em';
        descriptionElement.style.opacity = '0.4';
        descriptionElement.style.marginTop = '4px';
        descriptionElement.style.color = 'inherit';
        descriptionElement.setAttribute('data-field', col.field);

        const description = this.getDropdownDescription(col, value);
        descriptionElement.textContent = description;

        containerElement.appendChild(descriptionElement);
    }

    private updateDropdownDescription(col: ColDef, value: any, containerElement: HTMLElement): void {
        const descriptionElement = containerElement.querySelector(`[data-field="${col.field}"]`) as HTMLElement;
        if (descriptionElement) {
            const description = this.getDropdownDescription(col, value);
            descriptionElement.textContent = description;
        }
    }

    private getDropdownDescription(col: ColDef, value: any): string {
        if (value == null || value === '') {
            return '';
        }

        const allowedValues = col.cellEditorParams?.allowedValues || col.cellRendererParams?.allowedValues || [];
        const isListManagerStructure = col.cellEditorParams?.isListManagerStructure || col.cellRendererParams?.isListManagerStructure || false;
        const allowedValueKey = col.cellEditorParams?.allowedValueKey || col.cellRendererParams?.allowedValueKey;

        // For object dropdowns, extract the display value using allowedValueKey
        if (allowedValueKey && typeof value === 'object' && value !== null) {
            // Find matching object in allowedValues array
            const matchingOption = allowedValues.find((option: any) => {
                if (typeof option === 'object' && option !== null) {
                    return option[allowedValueKey] === value[allowedValueKey];
                }
                return false;
            });
            // For ListManager structure, get description from the object
            if (matchingOption && isListManagerStructure && matchingOption.description) {
                return matchingOption.description;
            }
            return '';
        } else {
            // For string dropdowns
            const valueStr = String(value);
            if (valueStr.includes(';')) {
                // Multi-select: show all descriptions
                const selectedValues = valueStr.split(/; ?/).filter(v => v.trim());
                const descParts: string[] = [];
                selectedValues.forEach(val => {
                    const trimmed = val.trim();
                    if (isListManagerStructure) {
                        // Find matching LabelDescription object
                        const matchingOption = allowedValues.find((option: any) =>
                            option && option.label === trimmed
                        );
                        if (matchingOption && matchingOption.description) {
                            descParts.push(matchingOption.description);
                        }
                    }
                });
                return descParts.join('; ');
            } else {
                // Single select
                if (isListManagerStructure) {
                    // Find matching LabelDescription object
                    const matchingOption = allowedValues.find((option: any) =>
                        option && option.label === valueStr
                    );
                    return matchingOption?.description || '';
                }
                return '';
            }
        }
    }

    isDate(value: any): value is Date {
        return value instanceof Date;
    }

    formatValue(value: any): string {
        if (this.isDate(value)) {
            return value.toLocaleDateString();
        }
        return value?.toString?.() ?? value ?? '';
    }

    getDisplayValue(col: ColDef, value: any): string {
        // Use valueFormatter if available (same as grid display)
        if (col.valueFormatter && col.valueFormatter instanceof Function) {
            return col.valueFormatter({value: value} as any) || '';
        }

        // For object dropdowns, extract the allowedValueKey property
        const allowedValueKey = col.cellEditorParams?.allowedValueKey;
        if (allowedValueKey && value && typeof value === 'object' && value !== null) {
            value = value[allowedValueKey];
        }

        // For string values (including ListManager dropdowns), return as-is
        return this.formatValue(value);
    }

    getChangeMessage(col: ColDef): string {
        if (!col || !this.originalRowData || this.originalRowData.state === 'new') {
            return '';
        }
        let originalValue = this.getDisplayValue(col, CommonHelperService.getDeepValue(this.originalRowData, col.field));
        const updatedValue = this.getDisplayValue(col, CommonHelperService.getDeepValue(this.updatedRowData, col.field));

        if (originalValue === updatedValue) {
            return null;
        }
        const maxWidth = 50;
        if (typeof originalValue === 'string' && originalValue.length > maxWidth) {
            originalValue = originalValue.substring(0, maxWidth - 3) + '...';
        }
        return `Original Value: ${originalValue}`;
    }

    getValidationMessage(col: ColDef): string {
        const fieldRule = this.fieldRulesByFieldName.get(col.field);
        const currentValue = this.updatedRowData[col.field];
        if (currentValue && col.cellEditorParams?.availableOptions) {
            const memberType = col.cellEditorParams?.memberType;
            const allowedValueKey = col.cellEditorParams?.allowedValueKey;
            const validOptions = this.validOptionsFor(col);

            // Normalize currentValue for comparison
            const maxSelectionCount = col.cellEditorParams?.maxSelectionCount ?? 1;
            const isMultiSelect = maxSelectionCount != 1;

            // Check if this is a multi-select (either multiSelectDropdown or objectDropdown with maxSelectionCount != 1)
            if ((memberType === 'multiSelectDropdown' ||
                 memberType === 'multiSelectIdsDropdown' ||
                 memberType === 'objectDropdown') && isMultiSelect) {
                // Multi-select: check each selected value
                const selectedValues = Array.isArray(currentValue)
                    ? currentValue.map((v: any) => String(v).trim()).filter((v: string) => v)
                    : String(currentValue).trim().split(/; ?/).map((v: string) => v.trim()).filter(v => v);
                const invalidValues = [];
                for (const val of selectedValues) {
                    if (!validOptions.has(val)) {
                        invalidValues.push("'" + val + "'");
                    }
                }
                if (invalidValues.length > 0) {
                    return `These values: [${invalidValues.join(',')}] are not allowed`;
                }
            } else if (memberType === 'objectDropdown' && allowedValueKey && typeof currentValue === 'object' && currentValue !== null) {
                // For object dropdowns with actual objects, compare by allowedValueKey
                const currentValueKey = currentValue[allowedValueKey];
                if (currentValueKey && !validOptions.has(String(currentValueKey).trim())) {
                    return `value '${currentValueKey}' is not allowed`;
                }
            } else if (!validOptions.has(String(currentValue).trim())) {
                // For string values or ListManager structures
                const normalizedCurrentValue = String(currentValue).trim();
                // Double-check: maybe availableOptions already contains the labels directly (as strings)
                const availableOptionsRaw = col.cellEditorParams.availableOptions;
                if (Array.isArray(availableOptionsRaw) && availableOptionsRaw.length > 0) {
                    // Check if first option is already a string (not an object)
                    const firstOption = availableOptionsRaw[0];
                    if (typeof firstOption === 'string') {
                        // availableOptions already contains strings, check directly
                        const stringOptions = availableOptionsRaw.map((opt: any) => String(opt).trim());
                        if (stringOptions.includes(normalizedCurrentValue)) {
                            // It's valid
                            return null;
                        }
                    } else if (typeof firstOption === 'object' && firstOption !== null && allowedValueKey) {
                        // It's an object, try extracting the key value
                        const extractedOptions = availableOptionsRaw.map((opt: any) => {
                            if (opt && typeof opt === 'object' && opt[allowedValueKey] !== undefined) {
                                return String(opt[allowedValueKey]).trim();
                            }
                            return opt ? String(opt).trim() : opt;
                        });
                        if (extractedOptions.includes(normalizedCurrentValue)) {
                            // It's valid
                            return null;
                        }
                    }
                }
                return `value '${currentValue}' is not allowed`;
            }
        }
        if (fieldRule) {
            return this.validationService.validateField(fieldRule, this.updatedRowData, false);
        }
        return null;
    }

    hasSubrowCell(col: ColDef | ColGroupDef): boolean {
        const facts = this.factsFor(col);
        if (facts.holdsSubrowCell === undefined) {
            facts.holdsSubrowCell = ViewDetailContentComponent.findSubrowCell(col);
        }
        return facts.holdsSubrowCell;
    }

    private static findSubrowCell(col: ColDef | ColGroupDef): boolean {
        if ((col as any).esciChildGrid === true) {
            return true;
        }
        if (!('children' in col) || !col.children || col.children.length === 0) {
            return false;
        }
        for (const childColumn of col.children) {
            if (!('field' in childColumn)) {
                continue;
            }
            const cellClass = (childColumn as ColDef).cellClass;
            if (typeof cellClass === 'string' && cellClass.includes('subrow-cell')) {
                return true;
            }
            if (Array.isArray(cellClass) && cellClass.some(c => c.includes('subrow-cell'))) {
                return true;
            }
        }
        return false;
    }

    private factsFor(col: ColDef | ColGroupDef): ColumnFacts {
        let facts = this.columnFacts.get(col);
        if (!facts) {
            facts = {};
            this.columnFacts.set(col, facts);
        }
        return facts;
    }

    /**
     * The values this column will accept, as a set. Which of them counts as the allowed value depends only on the
     * column - an object dropdown is compared by its allowedValueKey, anything else by the option itself - so the
     * set is built once and rebuilt only when the column is offered a different option list.
     */
    private validOptionsFor(col: ColDef): Set<any> {
        const availableOptions = col.cellEditorParams.availableOptions;
        const facts = this.factsFor(col);
        if (facts.validOptions && facts.validOptionSource === availableOptions) {
            return facts.validOptions;
        }
        const allowedValueKey = col.cellEditorParams?.allowedValueKey;
        const readsAllowedValueKey = col.cellEditorParams?.memberType === 'objectDropdown' && !!allowedValueKey;
        facts.validOptions = new Set<any>(availableOptions.map((opt: any) => {
            if (!readsAllowedValueKey) {
                return String(opt).trim();
            }
            if (opt && typeof opt === 'object' && opt[allowedValueKey] !== undefined) {
                return String(opt[allowedValueKey]).trim();
            }
            return opt ? String(opt).trim() : opt;
        }));
        facts.validOptionSource = availableOptions;
        return facts.validOptions;
    }

    getSingleColumnDetailFieldClass(col: ColDef | ColGroupDef): string {
        if (this.hasSubrowCell(col) || 'children' in col) {
            return 'detail-field-full';
        }
        const memberType = col.cellEditorParams?.memberType;
        const shouldSpanFullWidth = memberType === 'holeCriteria' || this.isWideTextBox(col);
        return shouldSpanFullWidth ? 'detail-field-full' : 'detail-field-half';
    }

    private isWideTextBox(col: ColDef): boolean {
        const editor = col.cellEditor;
        const isTextBox = col.cellEditorParams?.memberType === 'largeText' ||
                          !editor ||
                          editor === 'agTextCellEditor' ||
                          editor === 'agLargeTextCellEditor';
        return isTextBox && (col.width ?? 0) > 100;
    }

    onChildGridChange(updatedData: any) {
        // The child grid has already updated the data, just emit the change
        this.dataChange.emit(this.updatedRowData);
    }
}
